import { cgIntersect, cgIntersectOrNull } from '@civilgrid/utils/src/utilities/geometryHelpers';
import {
  area,
  bbox,
  bboxPolygon,
  bearingToAzimuth,
  buffer,
  centroid,
  circle,
  convertArea,
  destination,
  difference,
  distance,
  feature,
  featureCollection,
  lengthToDegrees,
  lineChunk,
  lineSegment,
  lineString,
  multiPolygon,
  nearestPointOnLine,
  point,
  polygon,
  polygonToLine,
  rhumbBearing,
  union,
} from '@turf/turf';
import type { Feature, LineString, MultiPolygon, Point, Polygon } from 'geojson';
import type { Project } from './types';

function getHypotAsLine(p1: Feature<Point>, p2: Feature<Point>) {
  return lineString([p1.geometry.coordinates, p2.geometry.coordinates]);
}

function logIntersectError(err: unknown, message: string) {
  console.error(message, err);
}

export function getGridsV1(boundary: MultiPolygon, sizeInAcres: number) {
  const [minX, minY, maxX, maxY] = bbox(boundary);

  const frames = [];
  const sideLength = lengthToDegrees(Math.sqrt(convertArea(sizeInAcres, 'acres', 'meters')), 'meters');

  let gridCounter = 0;
  for (let x = minX; x < maxX; x += sideLength) {
    for (let y = minY; y < maxY; y += sideLength) {
      const buffer = 1; // Eg. use 1.05 for some buffer;
      const sideLengthWithBuffer = buffer * sideLength;
      const p = polygon([
        [
          [x, y],
          [x + sideLengthWithBuffer, y],
          [x + sideLengthWithBuffer, y + sideLengthWithBuffer],
          [x, y + sideLengthWithBuffer],
          [x, y],
        ],
      ]);
      // `booleanIntersects` is a better option but there's a bug
      // https://github.com/Turfjs/turf/issues/2585
      if (
        cgIntersect(
          { label: 'boundary', feature: multiPolygon(boundary.coordinates) },
          { label: `polygon:${x},${y}`, feature: multiPolygon([p.geometry.coordinates]) },
        )
      ) {
        p.id = `${++gridCounter}`;
        frames.push(p);
      }
    }
  }

  return frames;
}

/**
 * Returns an array of grid polygons that cover the project boundary
 * @description This starts in the bottom left corner of the segment bbox, snaps to the nearest point on the boundary, and creates a frame from that point. It then removes the chunk from the boundary and recursively computes until no boundary remains.
 * @param boundary - The project boundary as a MultiPolygon
 * @param gridSize - The size of the grid in acres
 * @returns An array of grid polygons that cover the project boundary
 */
export function getGridsV1dot1(boundary: MultiPolygon) {
  const polygonSegments: Feature<Polygon>[] = boundary.coordinates.map(
    (p) => buffer(polygon(p), 50, { units: 'feet' }) as any,
  );

  const areaInSf = convertArea(30, 'acres', 'feet');
  const bboxRadius = Math.sqrt(areaInSf);

  const frames: Feature<Polygon>[] = [];

  polygonSegments.forEach((segment) => {
    const buildFramesForSegment = (segment: Feature<Polygon>, depth: number = 0) => {
      const segmentBbox = bbox(segment);
      const segmentLine = polygonToLine(segment);

      if (segmentLine.type === 'FeatureCollection') {
        // TODO: divvy up into segments
      } else {
        const nearestPointOnBoundary = nearestPointOnLine(segmentLine, point([segmentBbox[0], segmentBbox[1]]));

        const aoi = circle(nearestPointOnBoundary, bboxRadius, {
          steps: 100,
          units: 'feet',
        });

        const overlap = cgIntersect(
          { label: `aoi:depth=${depth}`, feature: aoi },
          { label: `segment:depth=${depth}`, feature: segment },
        );

        if (overlap) {
          let minX = Infinity;
          let minY = Infinity;
          if (overlap.geometry.type === 'MultiPolygon') {
            overlap.geometry.coordinates[0].forEach((s) => {
              s.forEach((position) => {
                minX = Math.min(minX, position[0]);
                minY = Math.min(minY, position[1]);
              });
            });
          } else {
            overlap.geometry.coordinates[0].forEach((position) => {
              minX = Math.min(minX, position[0]);
              minY = Math.min(minY, position[1]);
            });
          }

          if (minX !== Infinity && minY !== Infinity) {
            const bottomLeft = point([minX, minY]);
            const topLeft = destination(bottomLeft, bboxRadius, 0, {
              units: 'feet',
            });
            const topRight = destination(topLeft, bboxRadius, 90, {
              units: 'feet',
            });
            const bottomRight = destination(bottomLeft, bboxRadius, 90, {
              units: 'feet',
            });
            const bboxFrame = polygon([
              [
                bottomLeft.geometry.coordinates,
                topLeft.geometry.coordinates,
                topRight.geometry.coordinates,
                bottomRight.geometry.coordinates,
                bottomLeft.geometry.coordinates,
              ],
            ]);

            bboxFrame.id = `${frames.length + 1}`;
            frames.push(bboxFrame);

            let alreadyFramed: Feature<MultiPolygon | Polygon> | null = null;
            if (frames.length === 1) {
              alreadyFramed = frames[0];
            } else if (frames.length > 1) {
              alreadyFramed = union(featureCollection(frames));
            }

            if (alreadyFramed) {
              const newBoundary = difference(featureCollection([segment, alreadyFramed]));

              if (!newBoundary || depth > 1_000) {
                if (depth > 1_000) {
                  console.error('ERROR CREATING GRIDS: REACHED MAX DEPTH OF 1,000!');
                }

                return;
              } else {
                if (newBoundary.geometry.type === 'MultiPolygon') {
                  newBoundary.geometry.coordinates.forEach((s) => {
                    buildFramesForSegment(polygon(s), depth + 1);
                  });
                } else {
                  buildFramesForSegment(newBoundary as Feature<Polygon>, depth + 1);
                }
              }
            }
          }
        }
      }
    };

    buildFramesForSegment(segment);
  });

  return frames;
}

/**
 * Returns a frame for a cell overlap
 * @param cellOverlap - The cell overlap as a Polygon or MultiPolygon
 * @param bboxRadius - The radius of the bbox
 * @returns A frame for the cell overlap
 */
function getFrameForCell({
  cellOverlap,
  bboxWidth,
  bboxHeight,
}: {
  cellOverlap: Feature<Polygon | MultiPolygon>;
  bboxWidth: number;
  bboxHeight: number;
}) {
  // for the cell overlap, grab its bbox (it will be smaller than the grid cell/frame bbox)
  // this helps center the grid/cell up with the actual cell overlap so we don't get small-area frames
  const [cellMinX, cellMinY] = bbox(cellOverlap);

  // of the new small cell overlap bbox, grab the bottom left point (that will be the bbox base)
  const cellIntxBottomLeft = point([cellMinX, cellMinY]);
  const cellIntxTopLeft = destination(cellIntxBottomLeft, bboxHeight, 0, {
    units: 'feet',
  });
  const cellIntxTopRight = destination(cellIntxTopLeft, bboxWidth, 90, {
    units: 'feet',
  });

  // create a line along hypot to get the cell overlap's new bbox for adjustment
  const cellIntxHypotAsLine = getHypotAsLine(cellIntxBottomLeft, cellIntxTopRight);

  // create a bbox from the hypot line to get the cell overlap's new bbox for adjustment
  const cellIntxBbox = bbox(cellIntxHypotAsLine);

  // create a polygon from the bbox to get the cell overlap's new bbox for adjustment
  const cellIntxFrame = bboxPolygon(cellIntxBbox);

  // return the cell overlap's new bbox for adjustment
  return cellIntxFrame;
}

/**
 * Returns an array of grid polygons that cover the project boundary
 * @description This is a more efficient way to create grids by using the bbox of the cell overlap. It slices a row/column from the project polygon segment and computes grids based on the sliced overlap. It then removes the slice and recursively computes until no boundary remains.
 * @param boundary - The project boundary as a MultiPolygon
 * @param gridSize - The size of the grid in acres
 * @returns An array of grid polygons that cover the project boundary
 */
/** Escape hatch for the {@link getGridsV2} recursion; a boundary needing this many slices is degenerate, not large. */
const MAX_GRID_RECURSION_DEPTH = 100;

/** Smallest remainder (square feet) worth slicing again; below this a clipped segment is floating-point noise. */
const MIN_SEGMENT_AREA_SF = 1;

export function getGridsV2({
  boundary,
  gridSize,
  debug,
  aspectRatio,
}: {
  boundary: MultiPolygon;
  gridSize: number;
  debug?: boolean;
  aspectRatio: number;
}) {
  // hold each individual polygon segment of the boundary here
  const boundarySegments: Feature<Polygon>[] = [];
  const bboxAreaInSf = convertArea(gridSize, 'acres', 'feet');
  const bboxHeight = Math.sqrt(bboxAreaInSf / aspectRatio);
  const bboxWidth = bboxHeight * aspectRatio;

  const getNestedSegments = (buffers: (Feature<Polygon | MultiPolygon> | undefined)[]) => {
    buffers.forEach((b) => {
      if (b?.geometry.type === 'MultiPolygon') {
        getNestedSegments(b.geometry.coordinates.map((p) => polygon(p)));
      } else if (b?.geometry.type === 'Polygon') {
        boundarySegments.push(b as Feature<Polygon>);
      }
    });
  };

  // get each polygon segment and add a buffer to it (makes it look better)
  const buffered = boundary.coordinates.map((p) => buffer(polygon(p), 50, { units: 'feet' }));

  // separate each multi-polygon into its own polygon segment
  getNestedSegments(buffered);

  // frames are the "grids" we return
  const frames: Feature<Polygon>[] = [];

  const debugFrame = (f: Feature<Polygon>) => {
    if (debug === true) {
      f.id = `DEBUG - ${frames.length + 1}`;
      f.properties = {
        color: 'green',
        strokeWidth: 4,
        strokeStyle: 'dashed',
      };
      frames.push(f);
    }
  };

  // recursive function to build frames for an individual polygon segment
  // this function creates a row and column "slice" from the segment

  // for the row and columns, check if the row overlap width or column overlap height is larger
  // if the row overlap width is larger, then we need to create cell grids/frames from the row
  // if the column overlap height is larger, then we need to create cell grids/frames from the column

  // with the taken row/column above, calculate the number of cells needed for the row/column coverage based on overlap width/height
  // calculate the height/width of each cell based on the overlap width/height
  // create a cell grid/frame for each cell
  const buildFramesForSegment = ({ segment, depth = 0 }: { segment: Feature<Polygon>; depth: number }) => {
    // grab bbox for entire polygon segment
    const [segmentMinX, segmentMinY, segmentMaxX, segmentMaxY] = bbox(segment);

    // grab bbox points, bottom left will be the "point" of both the column and row
    const segmentBboxBottomLeft = point([segmentMinX, segmentMinY]);
    const segmentBboxTopLeft = point([segmentMinX, segmentMaxY]);
    const segmentBboxBottomRight = point([segmentMaxX, segmentMinY]);

    const segmentWidth = distance(segmentBboxBottomLeft, segmentBboxBottomRight, { units: 'feet' });
    const segmentHeight = distance(segmentBboxBottomLeft, segmentBboxTopLeft, {
      units: 'feet',
    });

    // if the entire segment can fit into a single grid cell, then just center the grid cell on the segment
    // depth === 0 determines if its a top-level segment (i.e. an entire original polygon) (created unnecessary frames if we allow this for trimmed segments)
    // segmentWidth <= bboxRadius && segmentHeight <= bboxRadius determines if the segment is smaller than the grid cell
    if (depth === 0 && segmentWidth <= bboxWidth && segmentHeight <= bboxHeight) {
      const segmentCenter = centroid(segment);

      // from centroid, find the bottom left point and top right point of the bbox
      const bboxBottomMiddle = destination(segmentCenter, bboxHeight / 2, 180, {
        units: 'feet',
      });

      const bboxBottomLeft = destination(bboxBottomMiddle, bboxWidth / 2, -90, {
        units: 'feet',
      });

      const bboxTopLeft = destination(bboxBottomLeft, bboxHeight, 0, {
        units: 'feet',
      });

      const bboxTopRight = destination(bboxTopLeft, bboxWidth, 90, {
        units: 'feet',
      });

      debugFrame(segment);

      // create a frame from the two points' bbox
      const frame = bboxPolygon(bbox(getHypotAsLine(bboxBottomLeft, bboxTopRight)));

      frame.id = `${frames.length + 1}`;
      frames.push(frame);
    }
    // else slice a row and column from the segment, then trim the original segment
    // and recursively iterate over the newly clipped segment until none remains.
    else {
      const columnTopRight = destination(segmentBboxTopLeft, bboxWidth, 90, {
        units: 'feet',
      });
      const columnBottomRight = destination(segmentBboxBottomLeft, bboxWidth, 90, { units: 'feet' });

      const rowTopRight = destination(segmentBboxBottomRight, bboxHeight, 0, {
        units: 'feet',
      });
      const rowTopLeft = destination(segmentBboxBottomLeft, bboxHeight, 0, {
        units: 'feet',
      });

      // create a column with the bbox width from the project polygon segment
      const gridColumn = polygon([
        [
          segmentBboxBottomLeft.geometry.coordinates,
          segmentBboxTopLeft.geometry.coordinates,
          columnTopRight.geometry.coordinates,
          columnBottomRight.geometry.coordinates,
          segmentBboxBottomLeft.geometry.coordinates,
        ],
      ]);

      // create a row with the bbox height from the project polygon segment
      const gridRow = polygon([
        [
          segmentBboxBottomLeft.geometry.coordinates,
          rowTopLeft.geometry.coordinates,
          rowTopRight.geometry.coordinates,
          segmentBboxBottomRight.geometry.coordinates,
          segmentBboxBottomLeft.geometry.coordinates,
        ],
      ]);

      // check if the column and row overlap with the project polygon segment
      // use cgIntersectOrNull, that way if the overlap is a "point", it won't throw an error
      // "point" overlaps can happen since we use a buffer on the boundary
      const columnOverlap = cgIntersectOrNull(
        { label: 'grid-column', feature: gridColumn },
        { label: `segment:depth=${depth}`, feature: segment },
        logIntersectError,
      );
      const rowOverlap = cgIntersectOrNull(
        { label: 'grid-row', feature: gridRow },
        { label: `segment:depth=${depth}`, feature: segment },
        logIntersectError,
      );

      let columnOverlapHeight = 0;
      let rowOverlapWidth = 0;

      // get bbox of the overlapping polygon portion within the column to get height
      if (columnOverlap) {
        const columnOverlapBbox = bbox(columnOverlap);
        const { coordinates } = bboxPolygon(columnOverlapBbox).geometry;
        const columnOverlapBottomLeft = point(coordinates[0][0]);
        const columnOverlapTopLeft = point(coordinates[0][3]);
        columnOverlapHeight = distance(columnOverlapBottomLeft, columnOverlapTopLeft, { units: 'feet' });
      }

      // get bbox of the overlapping polygon portion within the row to get width
      if (rowOverlap) {
        const rowOverlapBbox = bbox(rowOverlap);
        const { coordinates } = bboxPolygon(rowOverlapBbox).geometry;
        const rowOverlapBottomLeft = point(coordinates[0][0]);
        const rowOverlapBottomRight = point(coordinates[0][1]);
        rowOverlapWidth = distance(rowOverlapBottomLeft, rowOverlapBottomRight, {
          units: 'feet',
        });
      }

      // if the column overlap height is greater than the row overlap width
      // then we need to create cell grids/frames from the column
      if (columnOverlapHeight > rowOverlapWidth && !!columnOverlap) {
        debugFrame(gridColumn);
        // calculate the column overlap bbox for base points
        const columnBbox = bbox(columnOverlap);
        const { coordinates } = bboxPolygon(columnBbox).geometry;

        const columnBottomLeft = point(coordinates[0][0]);
        // calculate the minimum number of grids required to cover the column overlap
        const columnCellCount = Math.ceil(columnOverlapHeight / bboxHeight);
        const columnCellHeight = columnOverlapHeight / columnCellCount;

        // create a grid cell for each required cell above
        for (let i = 0; i < columnCellCount; i++) {
          // start at the bottom left and work our way up based on column cell height and current index
          const cellCenterY = i * columnCellHeight + columnCellHeight / 2;

          // cell bottom left is have the bbox width below the cell center y coord
          const cellBottomLeft = destination(columnBottomLeft, cellCenterY - bboxHeight / 2, 0, { units: 'feet' });

          const cellTopLeft = destination(cellBottomLeft, bboxHeight, 0, {
            units: 'feet',
          });
          const cellTopRight = destination(cellTopLeft, bboxWidth, 90, {
            units: 'feet',
          });

          // create a line along hypot to get the cell overlap's new bbox for adjustment
          const hypotAsLine = getHypotAsLine(cellBottomLeft, cellTopRight);
          const hypotBbox = bbox(hypotAsLine);
          const hypotBboxPolygon = bboxPolygon(hypotBbox);

          // see how much of the polygon segment overlaps within the created cell/frame
          const cellOverlap = cgIntersect(
            { label: `column-cell:depth=${depth},i=${i}`, feature: hypotBboxPolygon },
            { label: `segment:depth=${depth}`, feature: segment },
          );

          // if overlap exists (it should)
          if (cellOverlap) {
            // if the cell overlap is a multi-polygon, then we need to create
            // a new frame for each polygon "chunk" of the cell overlap,
            if (cellOverlap.geometry.type === 'MultiPolygon') {
              cellOverlap.geometry.coordinates.forEach((p) => {
                debugFrame(polygon(p));

                const frame = getFrameForCell({
                  cellOverlap: polygon(p),
                  bboxWidth,
                  bboxHeight,
                });

                frame.id = `${frames.length + 1}`;
                frames.push(frame);
              });
            } else {
              debugFrame(cellOverlap as Feature<Polygon>);
              // if the cell overlap is a single polygon, then we can just create a new frame for it
              const frame = getFrameForCell({
                cellOverlap,
                bboxWidth,
                bboxHeight,
              });
              frame.id = `${frames.length + 1}`;
              frames.push(frame);
            }
          }
        }
      }
      // if the row overlap width is greater than the column overlap height
      // then we need to create cell grids/frames from the row
      else if (rowOverlap) {
        debugFrame(gridRow);
        // calculate the row overlap bbox for base points
        const rowBbox = bbox(rowOverlap);
        const { coordinates } = bboxPolygon(rowBbox).geometry;

        const rowBottomLeft = point(coordinates[0][0]);
        // calculate the minimum number of grids required to cover the row overlap
        const rowCellCount = Math.ceil(rowOverlapWidth / bboxWidth);
        const rowCellWidth = rowOverlapWidth / rowCellCount;

        // create a grid cell for each required cell above
        for (let i = 0; i < rowCellCount; i++) {
          // start at the bottom left and work our way to the right based on row cell width and current index
          const cellCenterX = i * rowCellWidth + rowCellWidth / 2;

          // cell bottom left is have the bbox width to the left of the cell center x coord
          const cellBottomLeft = destination(
            rowBottomLeft,
            // cellCenterX - bboxRadius / 2,
            cellCenterX - bboxWidth / 2,
            90,
            { units: 'feet' },
          );

          // go 45 degrees to the top-right to create the top right point
          const cellTopRight = destination(cellBottomLeft, Math.hypot(bboxWidth, bboxHeight), 45, { units: 'feet' });

          // create a line along hypot to get the cell overlap's new bbox for adjustment
          const hypotAsLine = getHypotAsLine(cellBottomLeft, cellTopRight);
          const hypotBbox = bbox(hypotAsLine);
          const hypotBboxPolygon = bboxPolygon(hypotBbox);

          // see how much of the polygon segment overlaps within the created cell/frame
          const cellOverlap = cgIntersect(
            { label: `row-cell:depth=${depth},i=${i}`, feature: hypotBboxPolygon },
            { label: `segment:depth=${depth}`, feature: segment },
          );

          // if overlap exists (it should)
          if (cellOverlap) {
            // if the cell overlap is a multi-polygon, then we need to create
            // a new frame for each polygon "chunk" of the cell overlap,
            if (cellOverlap.geometry.type === 'MultiPolygon') {
              cellOverlap.geometry.coordinates.forEach((p) => {
                debugFrame(polygon(p));
                const frame = getFrameForCell({
                  cellOverlap: polygon(p),
                  bboxWidth,
                  bboxHeight,
                });
                frame.id = `${frames.length + 1}`;
                frames.push(frame);
              });
            } else {
              debugFrame(cellOverlap as Feature<Polygon>);
              // if the cell overlap is a single polygon, then we can just create a new frame for it
              const frame = getFrameForCell({
                cellOverlap,
                bboxWidth,
                bboxHeight,
              });
              frame.id = `${frames.length + 1}`;
              frames.push(frame);
            }
          }
        }
      }

      // "isFramed" is the already-covered area of the original polygon segment
      // i.e. it is the union of all the already-created frames
      let isFramed: Feature<Polygon | MultiPolygon> | null = null;

      // @turf/union is requires more than feature to work, so we need to check if there is only one frame
      if (frames.length === 1) {
        // if there is only one frame, then that frame is the already-covered area
        isFramed = frames[0];
      } else if (frames.length > 1) {
        // if there is more than one frame, then we need to union them
        // all together into a single polygon/multi-polygon feature
        isFramed = union(featureCollection(frames));
      }

      if (isFramed) {
        // take the current working segment and remove the already-covered area
        const clippedSegment = difference(featureCollection([segment, isFramed]));
        // if no clipped segment exists, then we have reached the end of the working polygon segment
        if (!clippedSegment) {
          return;
        }

        const segmentArea = convertArea(area(segment), 'meters', 'feet');
        const clippedSegmentArea = convertArea(area(clippedSegment), 'meters', 'feet');

        // @turf/difference leaves floating-point slivers whose row and column strips produce no new
        // frames, which leaves the clip identical to its own segment and recurses until the depth cap
        if (clippedSegmentArea >= segmentArea) {
          if (clippedSegmentArea >= MIN_SEGMENT_AREA_SF) {
            console.warn(
              `Grid generation made no progress at depth ${depth}; abandoning ${clippedSegmentArea.toFixed(2)} sf of uncovered boundary`,
            );
          }

          return;
        }

        // escape hatch to prevent infinite loops
        if (depth >= MAX_GRID_RECURSION_DEPTH) {
          console.error(
            `ERROR CREATING GRIDS: REACHED MAX DEPTH OF ${MAX_GRID_RECURSION_DEPTH}! ${clippedSegmentArea.toFixed(2)} sf of boundary left uncovered after ${frames.length} frames`,
          );

          return;
        }

        const clippedChunks =
          clippedSegment.geometry.type === 'MultiPolygon'
            ? clippedSegment.geometry.coordinates.map((p) => polygon(p))
            : [clippedSegment as Feature<Polygon>];

        // if a portion of the polygon segment still exists after removing the framed area,
        // recursively iterate over the newly clipped segment until none remains
        for (const chunk of clippedChunks) {
          // a chunk this small is a degenerate polygon, not a real remainder of the boundary
          if (convertArea(area(chunk), 'meters', 'feet') < MIN_SEGMENT_AREA_SF) {
            continue;
          }

          buildFramesForSegment({
            segment: chunk,
            depth: depth + 1,
          });
        }
      }
    }
  };

  // initial entry for the polygon boundary segments
  boundarySegments.forEach((s) => {
    buildFramesForSegment({
      segment: s,
      depth: 0,
    });
  });

  // extra check since we add a buffer to the boundary segments above
  // if the frame does not DIRECTLY intersect with the ACTUAL polygon boundary at all,
  // then remove it completely. Side-effect of adding the buffer.
  return frames.filter((f) => {
    if (
      cgIntersect(
        { label: 'boundary', feature: multiPolygon(boundary.coordinates) },
        { label: `frame:${f.id}`, feature: multiPolygon([f.geometry.coordinates]) },
      )
    ) {
      return true;
    }

    return false;
  });
}

// breaks the linear geometry into two-point segments, creates many more frames
function getLinearGridsV1(linear: NonNullable<Project['linear']>, gridSize: number) {
  const { geometry: multiLine, buffer } = linear;
  const bboxAreaInSf = convertArea(gridSize, 'acres', 'feet');
  const bboxRadius = Math.sqrt(bboxAreaInSf);
  const frames: Feature<Polygon>[] = [];

  const lineSegments: Feature<LineString>[] = lineSegment(multiLine).features.map((f) => {
    const { coordinates } = f.geometry;
    const endIndex = coordinates.length - 1;
    const firstPoint = point(coordinates[0]);
    const secondPoint = point(coordinates[1]);
    const inverseStartBearing = bearingToAzimuth(rhumbBearing(secondPoint, firstPoint));

    const startDistance = distance(firstPoint, secondPoint, {
      units: 'feet',
    });
    const newFirstPoint = destination(secondPoint, startDistance + buffer.offset, inverseStartBearing, {
      units: 'feet',
    });

    const lastPoint = point(coordinates[endIndex]);
    const secondLastPoint = point(coordinates[endIndex - 1]);

    const inverseEndBearing = bearingToAzimuth(rhumbBearing(secondLastPoint, lastPoint));
    const endDistance = distance(lastPoint, secondLastPoint, {
      units: 'feet',
    });

    const newLastPoint = destination(secondLastPoint, endDistance + buffer.offset, inverseEndBearing, {
      units: 'feet',
    });

    const newSegment = lineString(coordinates);
    newSegment.geometry.coordinates[0] = newFirstPoint.geometry.coordinates;
    newSegment.geometry.coordinates[endIndex] = newLastPoint.geometry.coordinates;

    return newSegment;
  });

  lineSegments.forEach((segment) => {
    const chunked = lineChunk(segment, bboxRadius, { units: 'feet' });
    chunked.features.forEach((chunk) => {
      const chunkedBbox = bbox(chunk);
      const chunkedBboxPolygon = bboxPolygon(chunkedBbox);
      const bboxCenter = centroid(chunkedBboxPolygon);
      const bboxBottomLeft = destination(bboxCenter, Math.hypot(bboxRadius, bboxRadius) / 2, -135, { units: 'feet' });

      const bboxTopRight = destination(bboxBottomLeft, Math.hypot(bboxRadius, bboxRadius), 45, { units: 'feet' });

      const hypotAsLine = getHypotAsLine(bboxBottomLeft, bboxTopRight);
      const hypotBbox = bbox(hypotAsLine);
      const frame = bboxPolygon(hypotBbox);
      frame.id = `${frames.length + 1}`;
      frames.push(frame);
    });
  });

  return frames;
}

/**
 * Returns an array of grid polygons that cover the linear geometry
 * @param boundary - The project boundary as a MultiPolygon
 * @param linear - The linear geometry from type Project['linear]
 * @param gridSize - The size of the grid in acres
 * @returns An array of grid polygons that cover the linear geometry
 */
export function getLinearGridsV3({
  boundary,
  linear,
  gridSize,
  aspectRatio,
}: {
  boundary: MultiPolygon;
  linear: NonNullable<Project['linear']>;
  gridSize: number;
  aspectRatio: number;
}) {
  const { geometry: linearGeometry, buffer } = linear;
  const bboxAreaInSf = convertArea(gridSize, 'acres', 'feet');
  const bboxHeight = Math.sqrt(bboxAreaInSf);
  const bboxWidth = bboxHeight * aspectRatio;

  const frames: Feature<Polygon>[] = [];

  // break multi-lineString geometry into individual lineStrings
  const lineSegments = linearGeometry.coordinates.map((ls) => {
    const line = lineString(ls);
    const endIndex = line.geometry.coordinates.length - 1;
    const firstPoint = point(line.geometry.coordinates[0]);
    const secondPoint = point(line.geometry.coordinates[1]);
    const inverseStartBearing = bearingToAzimuth(rhumbBearing(secondPoint, firstPoint));
    const startDistance = distance(firstPoint, secondPoint, { units: 'feet' });
    const newFirstPoint = destination(secondPoint, startDistance + buffer.offset, inverseStartBearing, {
      units: 'feet',
    });

    const lastPoint = point(line.geometry.coordinates[endIndex]);
    const secondLastPoint = point(line.geometry.coordinates[endIndex - 1]);
    const inverseEndBearing = bearingToAzimuth(rhumbBearing(secondLastPoint, lastPoint));
    const endDistance = distance(lastPoint, secondLastPoint, { units: 'feet' });
    const newLastPoint = destination(secondLastPoint, endDistance + buffer.offset, inverseEndBearing, {
      units: 'feet',
    });

    line.geometry.coordinates[0] = newFirstPoint.geometry.coordinates;
    line.geometry.coordinates[endIndex] = newLastPoint.geometry.coordinates;
    return line;
  });

  lineSegments.forEach((segment) => {
    const chunked = lineChunk(segment, Math.min(bboxHeight, bboxWidth), {
      units: 'feet',
    });

    chunked.features.forEach((chunk) => {
      const chunkBbox = bbox(chunk);
      const chunkBboxPolygon = bboxPolygon(chunkBbox);
      const chunkBboxCenter = centroid(chunkBboxPolygon);
      const chunkBboxBottomMiddle = destination(chunkBboxCenter, bboxHeight / 2, 180, { units: 'feet' });

      const chunkBboxBottomLeft = destination(chunkBboxBottomMiddle, bboxWidth / 2, -90, { units: 'feet' });

      const chunkBboxTopLeft = destination(chunkBboxBottomLeft, bboxHeight, 0, {
        units: 'feet',
      });

      const chunkBboxTopRight = destination(chunkBboxTopLeft, bboxWidth, 90, {
        units: 'feet',
      });

      const hypotAsLine = getHypotAsLine(chunkBboxBottomLeft, chunkBboxTopRight);
      const frame = bboxPolygon(bbox(hypotAsLine));
      frame.id = `${frames.length + 1}`;
      frames.push(frame);
    });
  });

  let isFramed: Feature<MultiPolygon | Polygon> | null = null;
  if (frames.length === 1) {
    isFramed = frames[0];
  } else if (frames.length > 1) {
    isFramed = union(featureCollection(frames));
  }

  if (isFramed) {
    const missingArea = difference(featureCollection([feature(boundary), isFramed]));

    if (missingArea) {
      const missingGeometry =
        missingArea.geometry.type === 'Polygon'
          ? multiPolygon([missingArea.geometry.coordinates]).geometry
          : missingArea.geometry;

      const missingFrames = getGridsV2({
        boundary: missingGeometry,
        gridSize,
        aspectRatio,
      });

      missingFrames.forEach((f) => {
        f.id = `${frames.length + 1}`;
        frames.push(f);
      });
    }
  }

  return frames.filter((f) => {
    if (
      cgIntersect(
        { label: 'boundary', feature: multiPolygon(boundary.coordinates) },
        { label: `frame:${f.id}`, feature: multiPolygon([f.geometry.coordinates]) },
      )
    ) {
      return true;
    }

    return false;
  });
}

export function getGridsV3({
  boundary,
  gridSize,
  linear,
  aspectRatio = 1,
}: {
  boundary: MultiPolygon;
  gridSize: number;
  linear?: Project['linear'];
  aspectRatio?: number;
}) {
  if (!!linear && linear?.geometry?.type === 'MultiLineString') {
    return getLinearGridsV3({
      boundary,
      linear,
      gridSize,
      aspectRatio,
    });
  } else {
    return getGridsV2({ boundary, gridSize, aspectRatio });
  }
}

export function getGridsV4({
  boundary,
  gridSize,
  linear,
  aspectRatio = 4 / 3,
}: {
  boundary: MultiPolygon;
  gridSize: number;
  linear?: Project['linear'];
  aspectRatio?: number;
}) {
  let [frames, requestArea] =
    !!linear && linear?.geometry?.type === 'MultiLineString'
      ? [
          getLinearGridsV3({
            boundary,
            linear,
            gridSize,
            aspectRatio,
          }),
          buffer(feature(linear.geometry), linear.buffer.offset, {
            units: linear.buffer.units,
          }),
        ]
      : [
          getGridsV2({
            boundary,
            gridSize,
            aspectRatio,
          }),
          feature(boundary),
        ];

  if (!requestArea) {
    return frames;
  }

  const sortedFrames = frames
    .map((frame) => {
      const overlapArea = cgIntersectOrNull(
        { label: `frame:${frame.id}`, feature: frame },
        { label: 'request-area', feature: requestArea },
        logIntersectError,
      );
      return { frame, overlapArea: overlapArea ? area(overlapArea) : 0 };
    })
    .toSorted((a, b) => a.overlapArea - b.overlapArea)
    .map((f) => f.frame);

  for (const frame of sortedFrames) {
    if (frames.length === 1) {
      break;
    }

    const withoutCandidate = frames.filter((f) => f.id !== frame.id);
    const candidateUniqueArea = difference(featureCollection([frame, ...withoutCandidate]));
    if (
      !candidateUniqueArea ||
      !cgIntersect(
        { label: 'request-area', feature: requestArea },
        { label: `candidate-unique-area:frame=${frame.id}`, feature: candidateUniqueArea },
      )
    ) {
      frames = withoutCandidate;
    }
  }

  return frames;
}
